import React from 'react';
import { Box, Divider, Heading, Text, TextButton } from '@wix/design-system';
import { ExpandedText } from '../../components/expanded-text';
import type { BenefitType } from '../../components/benefit';
import { Benefit } from '../../components/benefit';
import { SiteSearch } from '@wix/wix-ui-icons-common/lazy';
import {
  useEssentials,
  withEssentialsContext,
} from '../../contexts/essentials-context';
import type { ExternalComponentProps } from '../types';
import { AppOverviewDataHooks } from './data-hook';
import { ExpandableText } from '../../components/expandable-text/expandable-text';
import { sizeConfig } from './sizeConfig.const';
import AppOverviewContent from './app-overview-components/app-overview-content';

export interface OverviewProps extends ExternalComponentProps {
  appName: string;
  description?: string;
  benefits?: BenefitType[];
  demoUrl?: string | undefined;
  onClickDemoUrl?: () => void;
  onExpandToggleClick?: (isExpanded: boolean) => void;
  collapsible: boolean;
  showHeading: boolean;
  onLearnMoreClick?: () => void;
  size: 'small' | 'large';
  rows: number;
  showAdaptiveLayout: boolean;
}

const AppOverviewComponent = ({
  appName,
  description,
  benefits,
  demoUrl,
  onClickDemoUrl,
  onExpandToggleClick,
  collapsible,
  showHeading,
  onLearnMoreClick,
  size,
  rows,
  showAdaptiveLayout,
}: OverviewProps) => {
  const { t } = useEssentials();

  if (!appName || appName === '') {
    return <></>;
  }

  const { headingSize, textSize, buttonSize, mainContainerGap } =
    sizeConfig[size];

  if (showAdaptiveLayout) {
    return (
      <Box
        direction="vertical"
        dataHook={AppOverviewDataHooks.OVERVIEW_CONTAINER}
        gap={mainContainerGap}
      >
        {showHeading && (
          <Heading
            size={headingSize}
            dataHook={AppOverviewDataHooks.APP_NAME_HEADER}
          >
            {t('app.page.overview.title', {
              name: appName,
              interpolation: { escapeValue: false },
            })}
          </Heading>
        )}

        {collapsible ? (
          <ExpandableText
            rows={rows}
            onExpandToggleClick={(isExpanded: boolean) =>
              onExpandToggleClick?.(isExpanded)
            }
            textSize={textSize}
            buttonSize={buttonSize}
            dataHook={AppOverviewDataHooks.INFORMATION_COLLAPSIBLE}
          >
            <AppOverviewContent
              size={size}
              benefits={benefits}
              description={description}
              onLearnMoreClick={onLearnMoreClick}
              appName={appName}
            />
          </ExpandableText>
        ) : (
          <Text
            size={textSize}
            weight="thin"
            dataHook={AppOverviewDataHooks.INFORMATION_NON_COLLAPSIBLE}
            marginBottom="24px"
          >
            <AppOverviewContent
              size={size}
              benefits={benefits}
              description={description}
              onLearnMoreClick={onLearnMoreClick}
              appName={appName}
            />
          </Text>
        )}
        {demoUrl && (
          <Box direction="vertical" gap="12px">
            <Divider />
            <TextButton
              prefixIcon={<SiteSearch />}
              as="a"
              href={demoUrl}
              onClick={onClickDemoUrl}
              target="_blank"
              dataHook={AppOverviewDataHooks.DEMO_URL}
            >
              {t('app.page.overview.demoLink')}
            </TextButton>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box direction="vertical">
      <Heading size="large" dataHook="overview-name">
        {t('app.page.overview.title', {
          name: appName,
          interpolation: { escapeValue: false },
        })}
      </Heading>
      <Box marginTop="24px" direction="vertical" align="left">
        {benefits?.map(
          (benefit, index) =>
            benefit.title &&
            benefit.title !== '' && (
              <Benefit key={index} title={benefit.title} />
            ),
        )}
      </Box>
      {collapsible ? (
        <ExpandedText
          text={description || ''}
          onExpandToggleClick={(isExpanded: boolean) =>
            onExpandToggleClick?.(isExpanded)
          }
        />
      ) : (
        <Box marginTop="24px">
          <Text
            weight="thin"
            dataHook="lean-overview-description"
            className="text-description"
          >
            {description}
          </Text>
        </Box>
      )}
      {demoUrl && (
        <Box direction="vertical" marginTop="24px" gap="12px">
          <Divider />
          <TextButton
            prefixIcon={<SiteSearch />}
            as="a"
            href={demoUrl}
            onClick={onClickDemoUrl}
            target="_blank"
          >
            {t('app.page.overview.demoLink')}
          </TextButton>
        </Box>
      )}
    </Box>
  );
};

export const AppOverview = withEssentialsContext(AppOverviewComponent);
